/**
 * Die Blickrichtung des Bretts: **jeder Spieler sieht seine eigene Farbe unten** (Zyklus R4).
 *
 * **Eine reine Darstellungsfrage, und sie gehört deshalb hierher und nicht in die Engine**
 * (`CLAUDE.md` Regel 9). Die Engine kennt keine Blickrichtung; Feldnamen bleiben kanonisch —
 * `green-A1` heißt bei jedem so, es steht nur woanders auf dem Schirm. Zwei Spieler, die über
 * dasselbe Feld reden, meinen dasselbe Feld.
 *
 * **Warum das exakt aufgeht.** Das Brett ist ein Sechseck um den Ursprung, die sechs Sektoren
 * sind Keile von je 36 Feldern. Ihre Schwerpunkte liegen (gemessen) bei **35,8° + k · 60°** —
 * eine Drehung um **k · 60°** bildet das Brett Feld für Feld auf sich selbst ab. Danach ist es
 * **bildgleich mit heute**, nur mit einer anderen Farbe unten: gleiche Umrisse, gleiches
 * `viewBox`, gleiche Feldgröße. Zoom und Pan (**E38**) rechnen deshalb unverändert weiter.
 *
 * **Warum es keine Feindrehung gibt.** Kein Keil sitzt exakt bei 90°; **Blau ist dem „unten" am
 * nächsten** (95,8°) und ist damit die Lage, in die der eigene Sektor gedreht wird. Den eigenen
 * Keil exakt nach unten zu kippen wäre eine Drehung um k · 60° − 5,8° — und sie zerstörte
 * **pointy-top**, das nach **E14** die Reihenbuchstaben A bis F trägt. Die Drehung ist deshalb
 * ganzzahlig in 60°-Schritten.
 *
 * **Gedreht werden Felder, nicht Pixel.** Die Rechnung ist exakt und ganzzahlig, in
 * Würfelkoordinaten; `hexToPixel` wird **nicht** angefasst. Das ist kein Stilpunkt: jene
 * Umrechnung ist Feld für Feld gegen `docs/board-reference.svg` geprüft, und die Grafik ist vom
 * User für Geometrie und Farben als verbindlich erklärt. Eine Drehung in Pixeln brächte
 * Fließkommafehler in eine Zusage, die heute exakt ist — und sie müsste an der Stelle ansetzen,
 * die am wenigsten angefasst werden darf.
 *
 * **Und sie dreht nur Positionen.** Die Figurensymbole, die Feldbeschriftungen und der
 * Figurenkreis drehen **nicht** mit: wer die ganze SVG-Gruppe drehte, bekäme Fahne, Schild und
 * Pfeile schief oder auf dem Kopf. Deshalb steht hier eine Abbildung **Feld → Feld** und kein
 * `transform` um alles. Dass die Figurenform davon unberührt bleibt, ist bei einer
 * 60°-Drehung sogar geometrisch geschenkt: die sechs Eckwinkel eines Feldes (30° + 60 i) gehen
 * dabei in sich über, die Feldform ist also identisch — es verschiebt sich nur ihr Mittelpunkt.
 */

import { SECTOR_RING, hex, type Hex, type SectorId } from '@flaghex/engine';

/**
 * Der Sektor, der in der kanonischen Lage unten liegt — **gemessen, nicht gesetzt**.
 *
 * Er fällt aus `hexToPixel` und der Keilform heraus: von den sechs Schwerpunkten bei
 * 35,8° + k · 60° ist Blau mit 95,8° dem „unten" (90°) am nächsten. `orientation.test.ts`
 * rechnet den Platz selbst aus und prüft gegen diesen Wert; eine Änderung an der
 * Hex→Pixel-Rechnung fiele dort auf, statt hier stillschweigend falsch zu werden.
 */
export const BOTTOM_SECTOR: SectorId = 'blue';

/** Die Anzahl der Sektoren am Brett — und damit der Drehschritte, bis es wieder von vorn geht. */
const STEPS = SECTOR_RING.length;

/**
 * Ein Feld um das Zentrum drehen, um `steps · 60°`.
 *
 * In Würfelkoordinaten ist eine 60°-Drehung eine Vertauschung mit Vorzeichenwechsel:
 * `(x, y, z) → (−z, −x, −y)`. Mit `x = q`, `z = r` und `y = −q − r` wird daraus
 * `(q, r) → (−r, q + r)` — ganzzahlig, ohne Winkelfunktion, ohne Rundung. Auf dem Bildschirm
 * ist ein Schritt **+60°** (y zeigt nach unten, der Winkel wächst also im Uhrzeigersinn).
 *
 * `steps` darf beliebig sein, auch negativ: gerechnet wird modulo sechs.
 */
export function rotateHex(cell: Hex, steps: number): Hex {
  let { q, r } = cell;
  const turns = ((steps % STEPS) + STEPS) % STEPS;
  for (let done = 0; done < turns; done++) {
    [q, r] = [-r, q + r];
  }
  return hex(q, r);
}

/**
 * Wie weit gedreht werden muss, damit `sector` unten liegt.
 *
 * **Ohne Sektor wird nicht gedreht** — und das ist kein Rückfall, sondern der Fall des neutralen
 * Testbretts (`#/board`): dort gibt es keinen Spieler, also keine Blickrichtung. Die
 * 217-Felder-Zusage des Abnahmelaufs darf nicht von einer Drehung abhängen.
 *
 * `SECTOR_RING` ist hier die **richtige** Liste: aufeinanderfolgende Einträge sind am Brett
 * benachbart, und ein Eintrag weiter sind genau 60°. `SECTOR_IDS` ist eine Aufzählung und sagt
 * über die Lage nichts — sie hier zu nehmen ergäbe eine Drehung, die für vier der sechs
 * Sektoren falsch ist und für zwei zufällig stimmt.
 */
export function viewSteps(sector: SectorId | undefined): number {
  if (sector === undefined) {
    return 0;
  }
  const from = SECTOR_RING.indexOf(sector);
  const home = SECTOR_RING.indexOf(BOTTOM_SECTOR);
  if (from < 0 || home < 0) {
    return 0;
  }
  return ((from - home) % STEPS + STEPS) % STEPS;
}
